import { useEffect, useMemo, useState } from "react"

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TablePagination,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"

import { DataGrid } from "@mui/x-data-grid"

import MainLayout from "../../layouts/MainLayout"

import {
  createOrganization,
  deleteOrganization,
  getOrganizationById,
  getOrganizationFormData,
  getOrganizations,
  updateOrganization,
} from "../../services/organizationService"

import { getBusinessLabel } from "../../config/appConfig"


// =========================================================
// BUSINESS LABELS
// =========================================================

const MAIN_COMPANY = getBusinessLabel(
  "mainCompany",
  "Entreprise principale"
)

const CENTRAL_ORGANIZATION = getBusinessLabel(
  "centralOrganization",
  "Organisation centrale"
)

const MAIN_DISTRIBUTOR = getBusinessLabel(
  "mainDistributor",
  "Distributeur principal"
)

const RELAY_DEPOT = getBusinessLabel(
  "relayDepot",
  "Structure secondaire"
)

const RELAY_DEPOTS = getBusinessLabel(
  "relayDepots",
  "Structures secondaires"
)

const FIELD_AGENTS = getBusinessLabel(
  "fieldAgents",
  "Agents commerciaux"
)


// =========================================================
// ORGANIZATION TYPES
// =========================================================

const CENTRAL_ORGANIZATION_TYPES = [
  "kin_mousse",
  "main_company",
]


const ORGANIZATION_CODE_PREFIXES = {
  kin_mousse: "ORG",
  main_company: "ORG",
  distributor: "DIST",
  relay_depot: "STR",
  commercial_team: "AGT",
  shop_group: "SHOP",
  warehouse: "WH",
}


// =========================================================
// DEFAULT FORM
// =========================================================

const defaultFormData = {
  company_id: 1,
  parent_id: "",
  code: "",
  name: "",
  organization_type: "kin_mousse",
  description: "",
  is_active: true,
}


// =========================================================
// HELPERS
// =========================================================

function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
}


function isCentralOrganizationType(value) {
  return CENTRAL_ORGANIZATION_TYPES.includes(
    normalizeText(value)
  )
}


function getOrganizationCodePrefix(
  organizationType
) {
  return (
    ORGANIZATION_CODE_PREFIXES[
      organizationType
    ] || "ORG"
  )
}


function generateOrganizationCode(
  organizationType,
  organizations = []
) {
  const prefix =
    getOrganizationCodePrefix(
      organizationType
    )

  const regex =
    new RegExp(
      `^${prefix}-(\\d+)$`,
      "i"
    )

  const maxNumber =
    organizations.reduce(
      (
        max,
        organization
      ) => {
        const code =
          String(
            organization.code || ""
          )
            .trim()
            .toUpperCase()

        const match =
          code.match(regex)

        if (!match) {
          return max
        }

        const number =
          Number(match[1])

        if (
          !Number.isFinite(number)
        ) {
          return max
        }

        return Math.max(
          max,
          number
        )
      },
      0
    )

  return `${prefix}-${String(
    maxNumber + 1
  ).padStart(3, "0")}`
}


function getDefaultParentId(
  organizationType,
  organizations = []
) {
  if (
    isCentralOrganizationType(
      organizationType
    )
  ) {
    return ""
  }

  const centralOrganization =
    organizations.find(
      (organization) => {
        const type =
          normalizeText(
            organization.organization_type
          )

        const code =
          String(
            organization.code || ""
          )
            .trim()
            .toUpperCase()

        return (
          CENTRAL_ORGANIZATION_TYPES.includes(
            type
          ) ||
          code ===
            "KIN-MOUSSE" ||
          organization.name ===
            CENTRAL_ORGANIZATION
        )
      }
    )

  return (
    centralOrganization?.id ||
    ""
  )
}


function getOrganizationTypeLabel(
  value,
  types = []
) {
  const labels = {
    kin_mousse:
      CENTRAL_ORGANIZATION,

    main_company:
      CENTRAL_ORGANIZATION,

    distributor:
      MAIN_DISTRIBUTOR,

    relay_depot:
      RELAY_DEPOT,

    commercial_team:
      FIELD_AGENTS,

    warehouse:
      "Entrepôt",

    shop_group:
      "Réseau de shops",
  }

  if (labels[value]) {
    return labels[value]
  }

  const found =
    types.find(
      (item) =>
        item.value === value
    )

  return (
    found?.label ||
    value ||
    "-"
  )
}


function getApiErrorMessage(
  error,
  fallback
) {
  const detail =
    error?.response?.data
      ?.detail

  if (
    typeof detail === "string"
  ) {
    return detail
  }

  if (
    Array.isArray(detail)
  ) {
    return detail
      .map(
        (item) =>
          item?.msg ||
          JSON.stringify(item)
      )
      .join(" | ")
  }

  return fallback
}


// =========================================================
// PAGE
// =========================================================

function OrganizationsPage() {
  const theme = useTheme()

  const isMdDown =
    useMediaQuery(
      theme.breakpoints.down("md")
    )

  const isSmDown =
    useMediaQuery(
      theme.breakpoints.down("sm")
    )


  // =======================================================
  // DATA
  // =======================================================

  const [
    organizations,
    setOrganizations,
  ] = useState([])

  const [
    companies,
    setCompanies,
  ] = useState([])

  const [
    parents,
    setParents,
  ] = useState([])

  const [
    organizationTypes,
    setOrganizationTypes,
  ] = useState([])


  // =======================================================
  // UI STATE
  // =======================================================

  const [
    loading,
    setLoading,
  ] = useState(false)

  const [
    saving,
    setSaving,
  ] = useState(false)

  const [
    open,
    setOpen,
  ] = useState(false)

  const [
    selectedOrganization,
    setSelectedOrganization,
  ] = useState(null)

  const [
    formData,
    setFormData,
  ] = useState({
    ...defaultFormData,
  })


  // =======================================================
  // FILTERS
  // =======================================================

  const [
    search,
    setSearch,
  ] = useState("")

  const [
    typeFilter,
    setTypeFilter,
  ] = useState("")

  const [
    statusFilter,
    setStatusFilter,
  ] = useState("")


  // =======================================================
  // PAGINATION
  // =======================================================

  const [
    paginationModel,
    setPaginationModel,
  ] = useState({
    page: 0,
    pageSize: 10,
  })


  // =======================================================
  // SNACKBAR
  // =======================================================

  const [
    snackbar,
    setSnackbar,
  ] = useState({
    open: false,
    severity: "success",
    message: "",
  })


  const showSnackbar = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      severity,
      message,
    })
  }


  const handleCloseSnackbar =
    () => {
      setSnackbar(
        (previous) => ({
          ...previous,
          open: false,
        })
      )
    }


  // =======================================================
  // LOAD ORGANIZATIONS
  // =======================================================

  const loadOrganizations =
    async () => {
      try {
        setLoading(true)

        const data =
          await getOrganizations()

        setOrganizations(
          Array.isArray(data)
            ? data
            : []
        )
      } catch (error) {
        console.error(
          "Error loading organizations:",
          error
        )

        showSnackbar(
          getApiErrorMessage(
            error,
            "Impossible de charger les organisations."
          ),
          "error"
        )
      } finally {
        setLoading(false)
      }
    }


  // =======================================================
  // LOAD FORM DATA
  // =======================================================

  const loadFormData =
    async () => {
      try {
        const data =
          await getOrganizationFormData()

        setCompanies(
          Array.isArray(
            data?.companies
          )
            ? data.companies
            : []
        )

        setParents(
          Array.isArray(
            data?.parents
          )
            ? data.parents
            : []
        )

        setOrganizationTypes(
          Array.isArray(
            data?.organization_types
          )
            ? data.organization_types
            : []
        )
      } catch (error) {
        console.error(
          "Error loading organization form data:",
          error
        )

        showSnackbar(
          getApiErrorMessage(
            error,
            "Impossible de charger les données du formulaire."
          ),
          "error"
        )
      }
    }


  useEffect(() => {
    loadOrganizations()
    loadFormData()
  }, [])


  // =======================================================
  // RESET PAGINATION AFTER FILTER
  // =======================================================

  useEffect(() => {
    setPaginationModel(
      (previous) => ({
        ...previous,
        page: 0,
      })
    )
  }, [
    search,
    typeFilter,
    statusFilter,
  ])


  // =======================================================
  // RESET FORM
  // =======================================================

  const resetForm = () => {
    const defaultType =
      "kin_mousse"

    setSelectedOrganization(
      null
    )

    setFormData({
      ...defaultFormData,

      company_id:
        companies[0]?.id ||
        1,

      organization_type:
        defaultType,

      parent_id:
        "",

      code:
        generateOrganizationCode(
          defaultType,
          organizations
        ),
    })
  }


  // =======================================================
  // OPEN ADD
  // =======================================================

  const openAddDialog = () => {
    resetForm()
    setOpen(true)
  }


  // =======================================================
  // OPEN EDIT
  // =======================================================

  const openEditDialog =
    async (
      organizationId
    ) => {
      try {
        const organization =
          await getOrganizationById(
            organizationId
          )

        setSelectedOrganization(
          organization
        )

        setFormData({
          company_id:
            organization.company_id ??
            companies[0]?.id ??
            1,

          parent_id:
            organization.parent_id ??
            "",

          code:
            organization.code ||
            "",

          name:
            organization.name ||
            "",

          organization_type:
            organization.organization_type ||
            "kin_mousse",

          description:
            organization.description ||
            "",

          is_active:
            typeof organization.is_active ===
            "boolean"
              ? organization.is_active
              : true,
        })

        setOpen(true)
      } catch (error) {
        console.error(error)

        showSnackbar(
          getApiErrorMessage(
            error,
            "Impossible de charger le détail de l'organisation."
          ),
          "error"
        )
      }
    }


  // =======================================================
  // CLOSE
  // =======================================================

  const handleClose = () => {
    if (saving) {
      return
    }

    setOpen(false)
    resetForm()
  }


  // =======================================================
  // FORM CHANGE
  // =======================================================

  const handleChange =
    (event) => {
      const {
        name,
        value,
      } = event.target

      if (
        name ===
        "organization_type"
      ) {
        setFormData(
          (previous) => ({
            ...previous,

            organization_type:
              value,

            code:
              selectedOrganization
                ? previous.code
                : generateOrganizationCode(
                    value,
                    organizations
                  ),

            parent_id:
              isCentralOrganizationType(
                value
              )
                ? ""
                : previous.parent_id ||
                  getDefaultParentId(
                    value,
                    organizations
                  ),
          })
        )

        return
      }

      if (
        name === "parent_id"
      ) {
        setFormData(
          (previous) => ({
            ...previous,

            parent_id:
              value === ""
                ? ""
                : Number(value),
          })
        )

        return
      }

      if (
        name === "company_id"
      ) {
        setFormData(
          (previous) => ({
            ...previous,

            company_id:
              value === ""
                ? ""
                : Number(value),
          })
        )

        return
      }

      if (
        name === "is_active"
      ) {
        setFormData(
          (previous) => ({
            ...previous,

            is_active:
              value === "true" ||
              value === true,
          })
        )

        return
      }

      setFormData(
        (previous) => ({
          ...previous,

          [name]:
            name === "code"
              ? value.toUpperCase()
              : value,
        })
      )
    }


  // =======================================================
  // VALIDATION
  // =======================================================

  const validateForm = () => {
    if (
      !formData.code?.trim()
    ) {
      showSnackbar(
        "Le code automatique de l'organisation n'a pas été généré.",
        "warning"
      )

      return false
    }

    if (
      !formData.name?.trim()
    ) {
      showSnackbar(
        "Le nom de l'organisation est obligatoire.",
        "warning"
      )

      return false
    }

    if (
      !formData.organization_type
    ) {
      showSnackbar(
        "Le type d'organisation est obligatoire.",
        "warning"
      )

      return false
    }

    if (
      selectedOrganization?.id &&
      Number(
        formData.parent_id
      ) ===
        Number(
          selectedOrganization.id
        )
    ) {
      showSnackbar(
        "Une organisation ne peut pas être son propre parent.",
        "warning"
      )

      return false
    }

    return true
  }


  // =======================================================
  // SAVE
  // =======================================================

  const handleSave =
    async () => {
      if (
        !validateForm()
      ) {
        return
      }

      const payload = {
        company_id:
          formData.company_id
            ? Number(
                formData.company_id
              )
            : null,

        parent_id:
          formData.parent_id
            ? Number(
                formData.parent_id
              )
            : null,

        code:
          formData.code
            .trim()
            .toUpperCase(),

        name:
          formData.name
            .trim(),

        organization_type:
          formData.organization_type,

        description:
          formData.description
            ?.trim() ||
          null,

        is_active:
          Boolean(
            formData.is_active
          ),
      }

      try {
        setSaving(true)

        if (
          selectedOrganization?.id
        ) {
          await updateOrganization(
            selectedOrganization.id,
            payload
          )

          showSnackbar(
            "Organisation modifiée avec succès."
          )
        } else {
          await createOrganization(
            payload
          )

          showSnackbar(
            "Organisation créée avec succès."
          )
        }

        await loadOrganizations()
        await loadFormData()

        /*
         * Important :
         * on ferme directement ici.
         * handleClose() refuserait de fermer
         * tant que saving === true.
         */
        setOpen(false)
        resetForm()
      } catch (error) {
        console.error(error)

        showSnackbar(
          getApiErrorMessage(
            error,
            "Impossible d'enregistrer l'organisation."
          ),
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  // =======================================================
  // DELETE
  // =======================================================

  const handleDelete =
    async (
      organizationId
    ) => {
      const organization =
        organizations.find(
          (item) =>
            Number(item.id) ===
            Number(
              organizationId
            )
        )

      const confirmed =
        window.confirm(
          `Supprimer l'organisation "${
            organization?.name ||
            organization?.code ||
            organizationId
          }" ?`
        )

      if (!confirmed) {
        return
      }

      try {
        await deleteOrganization(
          organizationId
        )

        showSnackbar(
          "Organisation supprimée avec succès."
        )

        await loadOrganizations()
        await loadFormData()
      } catch (error) {
        console.error(error)

        showSnackbar(
          getApiErrorMessage(
            error,
            "Impossible de supprimer l'organisation."
          ),
          "error"
        )
      }
    }


  // =======================================================
  // FILTER
  // =======================================================

  const filteredOrganizations =
    useMemo(() => {
      const keyword =
        search
          .trim()
          .toLowerCase()

      return organizations.filter(
        (organization) => {
          const typeLabel =
            getOrganizationTypeLabel(
              organization.organization_type,
              organizationTypes
            ).toLowerCase()

          const matchesSearch =
            keyword === "" ||
            String(
              organization.code ||
                ""
            )
              .toLowerCase()
              .includes(
                keyword
              ) ||
            String(
              organization.name ||
                ""
            )
              .toLowerCase()
              .includes(
                keyword
              ) ||
            String(
              organization.organization_type ||
                ""
            )
              .toLowerCase()
              .includes(
                keyword
              ) ||
            typeLabel.includes(
              keyword
            ) ||
            String(
              organization.parent_name ||
                ""
            )
              .toLowerCase()
              .includes(
                keyword
              )

          const matchesType =
            typeFilter === "" ||
            String(
              organization.organization_type ||
                ""
            ) ===
              typeFilter

          const matchesStatus =
            statusFilter === "" ||
            String(
              organization.is_active
            ) ===
              statusFilter

          return (
            matchesSearch &&
            matchesType &&
            matchesStatus
          )
        }
      )
    }, [
      organizations,
      search,
      typeFilter,
      statusFilter,
      organizationTypes,
    ])


  // =======================================================
  // MOBILE PAGINATION
  // =======================================================

  const paginatedOrganizations =
    useMemo(() => {
      const start =
        paginationModel.page *
        paginationModel.pageSize

      return filteredOrganizations.slice(
        start,
        start +
          paginationModel.pageSize
      )
    }, [
      filteredOrganizations,
      paginationModel,
    ])


  useEffect(() => {
    const totalPages =
      Math.max(
        1,
        Math.ceil(
          filteredOrganizations.length /
            paginationModel.pageSize
        )
      )

    if (
      paginationModel.page >=
      totalPages
    ) {
      setPaginationModel(
        (previous) => ({
          ...previous,

          page:
            totalPages - 1,
        })
      )
    }
  }, [
    filteredOrganizations.length,
    paginationModel.page,
    paginationModel.pageSize,
  ])


  // =======================================================
  // PARENT OPTIONS
  // =======================================================

  const parentOptions =
    useMemo(() => {
      return parents.filter(
        (item) => {
          if (
            !selectedOrganization?.id
          ) {
            return true
          }

          return (
            Number(item.id) !==
            Number(
              selectedOrganization.id
            )
          )
        }
      )
    }, [
      parents,
      selectedOrganization,
    ])


  const isCentralTypeSelected =
    isCentralOrganizationType(
      formData.organization_type
    )


  // =======================================================
  // DESKTOP COLUMNS
  // =======================================================

  const columns =
    useMemo(
      () => [
        {
          field:
            "name",

          headerName:
            "Organisation",

          minWidth:
            190,

          flex:
            1.2,

          renderCell:
            (params) => (
              <Stack
                spacing={0.1}
                sx={{
                  minWidth:
                    0,

                  width:
                    "100%",
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={800}
                  noWrap
                  title={
                    params.row
                      .name ||
                    ""
                  }
                >
                  {params.row
                    .name ||
                    "-"}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                >
                  {params.row
                    .code ||
                    "-"}{" "}
                  • ID #
                  {params.row.id}
                </Typography>
              </Stack>
            ),
        },

        {
          field:
            "organization_type",

          headerName:
            "Type",

          minWidth:
            165,

          flex:
            1,

          renderCell:
            (params) => (
              <Chip
                label={
                  getOrganizationTypeLabel(
                    params.value,
                    organizationTypes
                  )
                }
                size="small"
                color="primary"
                variant="outlined"
                sx={{
                  maxWidth:
                    "100%",

                  fontWeight:
                    800,

                  "& .MuiChip-label":
                    {
                      overflow:
                        "hidden",

                      textOverflow:
                        "ellipsis",
                    },
                }}
              />
            ),
        },

        {
          field:
            "rattachement",

          headerName:
            "Rattachement",

          minWidth:
            215,

          flex:
            1.25,

          sortable:
            false,

          renderCell:
            (params) => (
              <Stack
                spacing={0.1}
                sx={{
                  minWidth:
                    0,

                  width:
                    "100%",
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={700}
                  noWrap
                  title={
                    params.row
                      .parent_name ||
                    "Aucune organisation parent"
                  }
                >
                  {params.row
                    .parent_name ||
                    "Aucun parent"}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  title={
                    params.row
                      .company_name ||
                    ""
                  }
                >
                  {params.row
                    .company_name ||
                    "Entreprise non renseignée"}
                </Typography>
              </Stack>
            ),
        },

        {
          field:
            "is_active",

          headerName:
            "Statut",

          width:
            95,

          renderCell:
            (params) => (
              <Chip
                label={
                  params.value
                    ? "Actif"
                    : "Inactif"
                }
                color={
                  params.value
                    ? "success"
                    : "default"
                }
                size="small"
                variant={
                  params.value
                    ? "filled"
                    : "outlined"
                }
                sx={{
                  fontWeight:
                    800,
                }}
              />
            ),
        },

        {
          field:
            "actions",

          headerName:
            "Actions",

          width:
            192,

          sortable:
            false,

          filterable:
            false,

          disableColumnMenu:
            true,

          renderCell:
            (params) => (
              <Stack
                direction="row"
                spacing={0.7}
              >
                <Button
                  variant="contained"
                  disableElevation
                  size="small"
                  onClick={() =>
                    openEditDialog(
                      params.row.id
                    )
                  }
                  sx={{
                    minWidth:
                      80,
                  }}
                >
                  Modifier
                </Button>

                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() =>
                    handleDelete(
                      params.row.id
                    )
                  }
                  sx={{
                    minWidth:
                      90,
                  }}
                >
                  Supprimer
                </Button>
              </Stack>
            ),
        },
      ],
      [
        organizationTypes,
        organizations,
      ]
    )


  // =======================================================
  // RESET FILTERS
  // =======================================================

  const resetFilters = () => {
    setSearch("")
    setTypeFilter("")
    setStatusFilter("")

    setPaginationModel({
      page: 0,
      pageSize: 10,
    })
  }


  // =======================================================
  // RENDER
  // =======================================================

  return (
    <MainLayout>
      <Box
        sx={{
          minWidth:
            0,

          width:
            "100%",

          pb: {
            xs:
              2,

            md:
              4,
          },

          "& .MuiButton-root":
            {
              textTransform:
                "none",

              fontWeight:
                800,

              borderRadius:
                2.5,
            },

          "& .MuiOutlinedInput-root":
            {
              borderRadius:
                2.5,
            },
        }}
      >

        {/* =================================================
            HEADER
        ================================================= */}

        <Box
          sx={{
            display:
              "grid",

            gridTemplateColumns:
              {
                xs:
                  "1fr",

                sm:
                  "minmax(0, 1fr) auto",
              },

            alignItems:
              "start",

            columnGap:
              2,

            rowGap:
              1.25,

            mb:
              2.5,
          }}
        >
          <Box
            sx={{
              minWidth:
                0,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight:
                  900,

                color:
                  "#0f172a",

                lineHeight:
                  1.12,

                letterSpacing:
                  "-0.025em",

                fontSize:
                  {
                    xs:
                      "1.65rem",

                    sm:
                      "1.95rem",

                    md:
                      "2.1rem",
                  },
              }}
            >
              Organisations
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt:
                  0.45,

                maxWidth:
                  820,

                lineHeight:
                  1.55,
              }}
            >
              Gérez l'organisation centrale, le{" "}
              {MAIN_DISTRIBUTOR.toLowerCase()},
              les{" "}
              {RELAY_DEPOTS.toLowerCase()}{" "}
              et les équipes commerciales.
            </Typography>
          </Box>


          <Button
            variant="contained"
            disableElevation
            onClick={
              openAddDialog
            }
            sx={{
              minHeight:
                42,

              px:
                2.4,

              minWidth:
                {
                  xs:
                    "100%",

                  sm:
                    185,
                },

              justifySelf:
                {
                  xs:
                    "stretch",

                  sm:
                    "end",
                },

              boxShadow:
                "0 8px 18px rgba(37, 99, 235, 0.16)",
            }}
          >
            Ajouter organisation
          </Button>
        </Box>


        {/* =================================================
            FILTERS
        ================================================= */}

        <Paper
          variant="outlined"
          sx={{
            p: {
              xs:
                1.25,

              sm:
                1.5,
            },

            mb:
              2,

            borderRadius:
              3.5,

            borderColor:
              "rgba(148, 163, 184, 0.25)",

            boxShadow:
              "0 8px 24px rgba(15, 23, 42, 0.035)",
          }}
        >
          <Box
            sx={{
              display:
                "grid",

              gridTemplateColumns:
                {
                  xs:
                    "1fr",

                  sm:
                    "repeat(2, minmax(0, 1fr))",

                  lg:
                    "minmax(260px, 1.5fr) minmax(180px, 0.8fr) minmax(150px, 0.7fr) auto",
                },

              gap:
                1.2,

              alignItems:
                "center",
            }}
          >
            <TextField
              fullWidth
              size="small"
              label="Recherche"
              placeholder="Code, nom, type ou parent..."
              value={
                search
              }
              onChange={(
                event
              ) =>
                setSearch(
                  event.target
                    .value
                )
              }
            />


            <TextField
              select
              fullWidth
              size="small"
              label="Type"
              value={
                typeFilter
              }
              onChange={(
                event
              ) =>
                setTypeFilter(
                  event.target
                    .value
                )
              }
            >
              <MenuItem value="">
                Tous les types
              </MenuItem>

              {organizationTypes.map(
                (type) => (
                  <MenuItem
                    key={
                      type.value
                    }
                    value={
                      type.value
                    }
                  >
                    {getOrganizationTypeLabel(
                      type.value,
                      organizationTypes
                    )}
                  </MenuItem>
                )
              )}
            </TextField>


            <TextField
              select
              fullWidth
              size="small"
              label="Statut"
              value={
                statusFilter
              }
              onChange={(
                event
              ) =>
                setStatusFilter(
                  event.target
                    .value
                )
              }
            >
              <MenuItem value="">
                Tous
              </MenuItem>

              <MenuItem value="true">
                Actif
              </MenuItem>

              <MenuItem value="false">
                Inactif
              </MenuItem>
            </TextField>


            <Button
              variant="outlined"
              onClick={
                resetFilters
              }
              sx={{
                minHeight:
                  40,

                minWidth:
                  {
                    xs:
                      "100%",

                    lg:
                      115,
                  },
              }}
            >
              Réinitialiser
            </Button>
          </Box>
        </Paper>


        {/* =================================================
            ORGANIZATIONS
        ================================================= */}

        <Paper
          variant="outlined"
          sx={{
            borderRadius:
              3.5,

            overflow:
              "hidden",

            minWidth:
              0,

            borderColor:
              "rgba(148, 163, 184, 0.25)",

            boxShadow:
              "0 10px 28px rgba(15, 23, 42, 0.04)",
          }}
        >

          {/* SECTION HEADER */}

          <Box
            sx={{
              px: {
                xs:
                  1.5,

                sm:
                  2,
              },

              py:
                1.4,

              display:
                "flex",

              flexDirection:
                {
                  xs:
                    "column",

                  sm:
                    "row",
                },

              justifyContent:
                "space-between",

              alignItems:
                {
                  xs:
                    "flex-start",

                  sm:
                    "center",
                },

              gap:
                1,

              borderBottom:
                "1px solid",

              borderColor:
                "divider",

              bgcolor:
                "#ffffff",
            }}
          >
            <Box>
              <Typography
                fontWeight={900}
                color="#0f172a"
              >
                Structure organisationnelle
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Organisations et rattachements
                opérationnels de RLSys Business
              </Typography>
            </Box>


            <Chip
              size="small"
              variant="outlined"
              label={`${filteredOrganizations.length} organisation${
                filteredOrganizations.length >
                1
                  ? "s"
                  : ""
              }`}
              sx={{
                fontWeight:
                  800,
              }}
            />
          </Box>


          {/* =================================================
              DESKTOP
          ================================================= */}

          {!isMdDown ? (
            <Box
              sx={{
                width:
                  "100%",

                minWidth:
                  0,

                overflow:
                  "hidden",

                bgcolor:
                  "#ffffff",
              }}
            >
              <DataGrid
                autoHeight
                rows={
                  filteredOrganizations
                }
                columns={
                  columns
                }
                loading={
                  loading
                }
                disableRowSelectionOnClick
                disableColumnSelector
                pagination
                paginationModel={
                  paginationModel
                }
                onPaginationModelChange={
                  setPaginationModel
                }
                pageSizeOptions={[
                  10,
                ]}
                rowHeight={62}
                columnHeaderHeight={50}
                localeText={{
                  noRowsLabel:
                    "Aucune organisation enregistrée",

                  noResultsOverlayLabel:
                    "Aucun résultat",

                  MuiTablePagination:
                    {
                      labelRowsPerPage:
                        "Lignes par page",

                      labelDisplayedRows:
                        ({
                          from,
                          to,
                          count,
                        }) =>
                          `${from}–${to} sur ${count}`,
                    },
                }}
                sx={{
                  border:
                    0,

                  width:
                    "100%",

                  minWidth:
                    0,

                  color:
                    "#334155",

                  "& .MuiDataGrid-columnHeaders":
                    {
                      bgcolor:
                        "#f8fafc",

                      borderBottom:
                        "1px solid rgba(148, 163, 184, 0.22)",
                    },

                  "& .MuiDataGrid-columnHeader":
                    {
                      bgcolor:
                        "#f8fafc",

                      px:
                        1.2,
                    },

                  "& .MuiDataGrid-columnHeaderTitle":
                    {
                      fontWeight:
                        900,

                      color:
                        "#334155",

                      fontSize:
                        "0.8rem",
                    },

                  "& .MuiDataGrid-cell":
                    {
                      display:
                        "flex",

                      alignItems:
                        "center",

                      minWidth:
                        0,

                      px:
                        1.2,

                      borderColor:
                        "rgba(148, 163, 184, 0.14)",
                    },

                  "& .MuiDataGrid-row:hover":
                    {
                      bgcolor:
                        "#f8fbff",
                    },

                  "& .MuiDataGrid-footerContainer":
                    {
                      minHeight:
                        52,

                      bgcolor:
                        "#ffffff",

                      borderTop:
                        "1px solid rgba(148, 163, 184, 0.20)",
                    },

                  "& .MuiTablePagination-toolbar":
                    {
                      minHeight:
                        52,
                    },

                  "& .MuiDataGrid-scrollbar--horizontal":
                    {
                      display:
                        "none",
                    },

                  "& .MuiDataGrid-virtualScroller":
                    {
                      overflowX:
                        "hidden",
                    },

                  "& .MuiDataGrid-overlay":
                    {
                      bgcolor:
                        "#ffffff",
                    },
                }}
              />
            </Box>
          ) : (

            /* =================================================
               TABLET / MOBILE
            ================================================= */

            <Box
              sx={{
                p: {
                  xs:
                    1.25,

                  sm:
                    1.5,
                },

                bgcolor:
                  "#f8fafc",
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    py:
                      5,

                    textAlign:
                      "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Chargement des organisations...
                  </Typography>
                </Box>
              ) : paginatedOrganizations.length ===
                0 ? (
                <Box
                  sx={{
                    py:
                      5,

                    textAlign:
                      "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Aucune organisation trouvée.
                  </Typography>
                </Box>
              ) : (
                <Stack
                  spacing={
                    1.25
                  }
                >
                  {paginatedOrganizations.map(
                    (
                      organization
                    ) => (
                      <Paper
                        key={
                          organization.id
                        }
                        variant="outlined"
                        sx={{
                          p: {
                            xs:
                              1.5,

                            sm:
                              1.75,
                          },

                          borderRadius:
                            3,

                          bgcolor:
                            "#ffffff",

                          borderColor:
                            "rgba(148, 163, 184, 0.22)",
                        }}
                      >
                        <Stack
                          spacing={
                            1.4
                          }
                        >

                          {/* TOP */}

                          <Box
                            sx={{
                              display:
                                "flex",

                              justifyContent:
                                "space-between",

                              alignItems:
                                "flex-start",

                              gap:
                                1.5,
                            }}
                          >
                            <Box
                              sx={{
                                minWidth:
                                  0,
                              }}
                            >
                              <Typography
                                fontWeight={900}
                                color="#0f172a"
                                sx={{
                                  overflowWrap:
                                    "anywhere",
                                }}
                              >
                                {organization.name ||
                                  "-"}
                              </Typography>

                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {organization.code ||
                                  "-"}{" "}
                                • ID #
                                {
                                  organization.id
                                }
                              </Typography>
                            </Box>


                            <Chip
                              size="small"
                              label={
                                organization.is_active
                                  ? "Actif"
                                  : "Inactif"
                              }
                              color={
                                organization.is_active
                                  ? "success"
                                  : "default"
                              }
                              sx={{
                                fontWeight:
                                  800,
                              }}
                            />
                          </Box>


                          {/* TYPE */}

                          <Box>
                            <Chip
                              label={
                                getOrganizationTypeLabel(
                                  organization.organization_type,
                                  organizationTypes
                                )
                              }
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{
                                fontWeight:
                                  800,

                                maxWidth:
                                  "100%",
                              }}
                            />
                          </Box>


                          {/* INFO */}

                          <Box
                            sx={{
                              display:
                                "grid",

                              gridTemplateColumns:
                                {
                                  xs:
                                    "1fr",

                                  sm:
                                    "repeat(2, minmax(0, 1fr))",
                                },

                              gap:
                                1.25,
                            }}
                          >
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={700}
                              >
                                Organisation parent
                              </Typography>

                              <Typography
                                variant="body2"
                                fontWeight={700}
                              >
                                {organization.parent_name ||
                                  "Aucun parent"}
                              </Typography>
                            </Box>


                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={700}
                              >
                                Entreprise
                              </Typography>

                              <Typography
                                variant="body2"
                                fontWeight={700}
                              >
                                {organization.company_name ||
                                  "Non renseignée"}
                              </Typography>
                            </Box>


                            {organization.description ? (
                              <Box
                                sx={{
                                  gridColumn:
                                    {
                                      sm:
                                        "1 / -1",
                                    },
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  fontWeight={700}
                                >
                                  Description
                                </Typography>

                                <Typography
                                  variant="body2"
                                  sx={{
                                    mt:
                                      0.2,

                                    lineHeight:
                                      1.5,
                                  }}
                                >
                                  {
                                    organization.description
                                  }
                                </Typography>
                              </Box>
                            ) : null}
                          </Box>


                          {/* ACTIONS */}

                          <Box
                            sx={{
                              display:
                                "grid",

                              gridTemplateColumns:
                                {
                                  xs:
                                    "1fr",

                                  sm:
                                    "repeat(2, minmax(0, 1fr))",
                                },

                              gap:
                                1,
                            }}
                          >
                            <Button
                              variant="contained"
                              disableElevation
                              size="small"
                              onClick={() =>
                                openEditDialog(
                                  organization.id
                                )
                              }
                            >
                              Modifier
                            </Button>

                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={() =>
                                handleDelete(
                                  organization.id
                                )
                              }
                            >
                              Supprimer
                            </Button>
                          </Box>
                        </Stack>
                      </Paper>
                    )
                  )}
                </Stack>
              )}


              <TablePagination
                component="div"
                count={
                  filteredOrganizations.length
                }
                page={
                  paginationModel.page
                }
                onPageChange={(
                  _event,
                  newPage
                ) =>
                  setPaginationModel(
                    (previous) => ({
                      ...previous,

                      page:
                        newPage,
                    })
                  )
                }
                rowsPerPage={10}
                rowsPerPageOptions={[
                  10,
                ]}
                labelRowsPerPage="Lignes"
                labelDisplayedRows={({
                  from,
                  to,
                  count,
                }) =>
                  `${from}–${to} sur ${count}`
                }
                sx={{
                  mt:
                    1,

                  bgcolor:
                    "#ffffff",

                  borderRadius:
                    2.5,

                  "& .MuiTablePagination-toolbar":
                    {
                      px:
                        1,

                      minHeight:
                        52,
                    },

                  "& .MuiTablePagination-selectLabel":
                    {
                      display:
                        "none",
                    },

                  "& .MuiTablePagination-input":
                    {
                      display:
                        "none",
                    },
                }}
              />
            </Box>
          )}
        </Paper>
      </Box>


      {/* =================================================
          ADD / EDIT DIALOG
      ================================================= */}

      <Dialog
        open={
          open
        }
        onClose={
          handleClose
        }
        fullWidth
        maxWidth="md"
        fullScreen={
          isSmDown
        }
        PaperProps={{
          sx: {
            borderRadius:
              {
                xs:
                  0,

                sm:
                  4,
              },

            maxHeight:
              {
                xs:
                  "100dvh",

                sm:
                  "90dvh",
              },

            m:
              {
                xs:
                  0,

                sm:
                  2,
              },
          },
        }}
      >
        <DialogTitle
          sx={{
            pb:
              1.5,
          }}
        >
          <Typography
            fontWeight={900}
            fontSize={20}
            color="#0f172a"
          >
            {selectedOrganization
              ? "Modifier l'organisation"
              : "Ajouter une organisation"}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt:
                0.35,
            }}
          >
            {selectedOrganization
              ? "Modifiez la structure, le rattachement et les informations de cette organisation."
              : "Créez une nouvelle structure et définissez son rattachement dans RLSys Business."}
          </Typography>
        </DialogTitle>


        <DialogContent dividers>
          <Stack
            spacing={
              2
            }
            sx={{
              pt:
                0.5,
            }}
          >
            <Alert
              severity="info"
              variant="outlined"
              sx={{
                borderRadius:
                  3,
              }}
            >
              Le code est généré automatiquement selon le type
              d'organisation. Pour une petite entreprise, utilisez le type{" "}
              <strong>
                {CENTRAL_ORGANIZATION}
              </strong>.
            </Alert>


            {/* CODE + NAME */}

            <Box
              sx={{
                display:
                  "grid",

                gridTemplateColumns:
                  {
                    xs:
                      "1fr",

                    md:
                      "minmax(180px, 0.7fr) minmax(260px, 1.3fr)",
                  },

                gap:
                  2,
              }}
            >
              <TextField
                size="small"
                label="Code automatique"
                name="code"
                value={
                  formData.code
                }
                onChange={
                  handleChange
                }
                fullWidth
                required
                disabled={
                  saving
                }
                InputProps={{
                  readOnly:
                    true,
                }}
                helperText="ORG-001, DIST-001, STR-001..."
              />


              <TextField
                size="small"
                label="Nom de l'organisation"
                name="name"
                value={
                  formData.name
                }
                onChange={
                  handleChange
                }
                fullWidth
                required
                disabled={
                  saving
                }
              />
            </Box>


            {/* TYPE + PARENT */}

            <Box
              sx={{
                display:
                  "grid",

                gridTemplateColumns:
                  {
                    xs:
                      "1fr",

                    md:
                      "repeat(2, minmax(0, 1fr))",
                  },

                gap:
                  2,
              }}
            >
              <TextField
                select
                size="small"
                label="Type"
                name="organization_type"
                value={
                  formData.organization_type
                }
                onChange={
                  handleChange
                }
                fullWidth
                required
                disabled={
                  saving
                }
              >
                {organizationTypes.map(
                  (type) => (
                    <MenuItem
                      key={
                        type.value
                      }
                      value={
                        type.value
                      }
                    >
                      {getOrganizationTypeLabel(
                        type.value,
                        organizationTypes
                      )}
                    </MenuItem>
                  )
                )}
              </TextField>


              <TextField
                select
                size="small"
                label="Organisation parent"
                name="parent_id"
                value={
                  formData.parent_id
                }
                onChange={
                  handleChange
                }
                fullWidth
                disabled={
                  saving ||
                  isCentralTypeSelected
                }
                helperText={
                  isCentralTypeSelected
                    ? "Une organisation centrale n'a pas de parent."
                    : "Par défaut, l'organisation centrale est utilisée."
                }
              >
                <MenuItem value="">
                  Aucune
                </MenuItem>

                {parentOptions.map(
                  (parent) => (
                    <MenuItem
                      key={
                        parent.id
                      }
                      value={
                        parent.id
                      }
                    >
                      {parent.code} -{" "}
                      {parent.name}
                    </MenuItem>
                  )
                )}
              </TextField>
            </Box>


            {/* COMPANY + STATUS */}

            <Box
              sx={{
                display:
                  "grid",

                gridTemplateColumns:
                  {
                    xs:
                      "1fr",

                    md:
                      "repeat(2, minmax(0, 1fr))",
                  },

                gap:
                  2,
              }}
            >
              <TextField
                select
                size="small"
                label="Entreprise"
                name="company_id"
                value={
                  formData.company_id
                }
                onChange={
                  handleChange
                }
                fullWidth
                disabled={
                  saving
                }
              >
                <MenuItem value="">
                  Aucune
                </MenuItem>

                {companies.map(
                  (company) => (
                    <MenuItem
                      key={
                        company.id
                      }
                      value={
                        company.id
                      }
                    >
                      {company.name}
                    </MenuItem>
                  )
                )}
              </TextField>


              <TextField
                select
                size="small"
                label="Statut"
                name="is_active"
                value={
                  String(
                    formData.is_active
                  )
                }
                onChange={
                  handleChange
                }
                fullWidth
                disabled={
                  saving
                }
              >
                <MenuItem value="true">
                  Actif
                </MenuItem>

                <MenuItem value="false">
                  Inactif
                </MenuItem>
              </TextField>
            </Box>


            {/* DESCRIPTION */}

            <TextField
              label="Description"
              name="description"
              value={
                formData.description
              }
              onChange={
                handleChange
              }
              fullWidth
              multiline
              minRows={3}
              disabled={
                saving
              }
              placeholder="Décrivez le rôle de cette organisation dans l'entreprise."
            />
          </Stack>
        </DialogContent>


        <DialogActions
          sx={{
            px: {
              xs:
                2,

              sm:
                3,
            },

            py:
              2,

            gap:
              1,

            borderTop:
              "1px solid",

            borderColor:
              "divider",
          }}
        >
          <Button
            onClick={
              handleClose
            }
            disabled={
              saving
            }
          >
            Annuler
          </Button>

          <Button
            variant="contained"
            disableElevation
            onClick={
              handleSave
            }
            disabled={
              saving
            }
          >
            {saving
              ? "Enregistrement..."
              : selectedOrganization
              ? "Modifier"
              : "Créer"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* =================================================
          SNACKBAR
      ================================================= */}

      <Snackbar
        open={
          snackbar.open
        }
        autoHideDuration={
          3500
        }
        onClose={
          handleCloseSnackbar
        }
        anchorOrigin={{
          vertical:
            "top",

          horizontal:
            "right",
        }}
      >
        <Alert
          onClose={
            handleCloseSnackbar
          }
          severity={
            snackbar.severity
          }
          variant="filled"
          sx={{
            width:
              "100%",

            borderRadius:
              3,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  )
}


export default OrganizationsPage